// src/app/modules/Crewing/components/CrewUploadModal.tsx
import React, { FC, useState, useEffect } from 'react'
import { useAuth } from '../../auth'
import * as XLSX from 'xlsx'
import jsPDF from 'jspdf'
import 'jspdf-autotable'
import {
  downloadCrewTemplate,
  previewCrewUpload,
  startCrewUploadJob,
  fetchCrewUploadJob,
  fetchActiveCrewUploadJob,
  cancelCrewUploadJob,
} from '../core/_requests'
import { BulkUploadResultDto, BulkUploadError, BulkUploadJob } from '../core/_models'
import { toast } from 'react-toastify'
import { KTSVG } from '../../../../_metronic/helpers'

const LABEL = 'form-label fw-semibold fs-6 mb-2 text-dark'
const WRAP_STYLE: React.CSSProperties = {
  background: 'rgba(0,0,0,0.5)',
  position: 'fixed',
  inset: 0,
  zIndex: 1050,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}

const STORAGE_KEY = 'crewUploadJobId'

const formatEta = (seconds?: number | null) => {
  if (!seconds || seconds <= 0) return null
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins}m ${secs}s`
}

interface CrewUploadModalProps {
  visible: boolean
  onClose: () => void
  onSuccess?: () => void
}

type UploadStep = 'UPLOAD' | 'PREVIEW' | 'RESULT'

export const CrewUploadModal: FC<CrewUploadModalProps> = ({
  visible,
  onClose,
  onSuccess,
}) => {
  const { currentUser } = useAuth()
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [currentStep, setCurrentStep] = useState<UploadStep>('UPLOAD')
  const [previewing, setPreviewing] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [preview, setPreview] = useState<BulkUploadResultDto | null>(null)
  const [uploadJob, setUploadJob] = useState<BulkUploadJob | null>(null)
  const [showPreviewErrors, setShowPreviewErrors] = useState(false)
  const [showJobErrors, setShowJobErrors] = useState(false)
  const isUploadActive =
    uploadJob?.status === 'QUEUED' || uploadJob?.status === 'RUNNING'
  const lastSuccessJobIdRef = React.useRef<number | null>(null)

  const resetUploadState = () => {
    localStorage.removeItem(STORAGE_KEY)
    setUploadJob(null)
    setPreview(null)
    setSelectedFile(null)
    setCurrentStep('UPLOAD')
  }

  useEffect(() => {
    if (!visible) {
      setCurrentStep('UPLOAD')
      setSelectedFile(null)
      setPreview(null)
      setUploadJob(null)
      setShowPreviewErrors(false)
      setShowJobErrors(false)
    } else {
      loadActiveUploadJob()
    }
  }, [visible])

  useEffect(() => {
    if (!uploadJob?.jobId) return
    const isActive = uploadJob.status === 'QUEUED' || uploadJob.status === 'RUNNING'
    if (!isActive) return

    const interval = window.setInterval(async () => {
      try {
        const updated = await fetchCrewUploadJob(uploadJob.jobId)
        setUploadJob(updated)
      } catch (error) {
        // ignore
      }
    }, 3000)

    return () => window.clearInterval(interval)
  }, [uploadJob?.jobId, uploadJob?.status])

  useEffect(() => {
    if (!uploadJob?.jobId) return
    if (uploadJob.status !== 'SUCCESS') return
    if (lastSuccessJobIdRef.current === uploadJob.jobId) return
    lastSuccessJobIdRef.current = uploadJob.jobId
    onSuccess?.()
  }, [uploadJob?.jobId, uploadJob?.status, onSuccess])

  const loadActiveUploadJob = async () => {
    try {
      const storedId = localStorage.getItem(STORAGE_KEY)
      if (storedId) {
        try {
          const storedJob = await fetchCrewUploadJob(Number(storedId))
          setUploadJob(storedJob)
          setCurrentStep('RESULT')
          return
        } catch (error) {
          localStorage.removeItem(STORAGE_KEY)
        }
      }
      const active = await fetchActiveCrewUploadJob()
      if (active?.jobId) {
        setUploadJob(active)
        setCurrentStep('RESULT')
        localStorage.setItem(STORAGE_KEY, String(active.jobId))
      }
    } catch (error) {
      // ignore
    }
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isUploadActive) {
      toast.warning('An upload is already in progress. Please wait for it to complete.')
      return
    }
    const file = e.target.files?.[0]
    if (file) {
      const validExtensions = ['.xlsx', '.xls', '.csv']
      const extension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase()
      if (!validExtensions.includes(extension)) {
        toast.error('Please select an Excel (.xlsx, .xls) or CSV (.csv) file')
        return
      }
      setSelectedFile(file)
      setPreview(null)
      setCurrentStep('UPLOAD')
    }
  }

  const handleDownloadTemplate = async () => {
    try {
      const blob = await downloadCrewTemplate()
      
      if ('showSaveFilePicker' in window) {
        const handle = await (window as any).showSaveFilePicker({
          suggestedName: 'Crew_Bulk_Upload_Template.xlsx',
          types: [{
            description: 'Excel File',
            accept: {'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx']}
          }]
        })
        const writable = await handle.createWritable()
        await writable.write(blob)
        await writable.close()
        toast.success('Template downloaded successfully')
      } else {
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', 'Crew_Bulk_Upload_Template.xlsx')
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
        setTimeout(() => {
          toast.success('Template downloaded successfully')
        }, 1000)
      }
    } catch (error: any) {
      if (error.name === 'AbortError') return
      console.error('Error downloading template:', error)
      toast.error('Failed to download template')
    }
  }

  const handlePreview = async () => {
    if (isUploadActive) {
      toast.warning('An upload is already in progress. Please wait for it to complete.')
      return
    }
    if (!selectedFile) {
      toast.error('Please select a file')
      return
    }

    setPreviewing(true)
    try {
      const previewData = await previewCrewUpload(selectedFile)
      setPreview(previewData)
      setCurrentStep('PREVIEW')
      
      if (previewData.errorCount > 0) {
        toast.warning(
          `Preview completed with ${previewData.errorCount} error(s). Please review before importing.`,
          { autoClose: 5000 }
        )
      } else {
        toast.success(`Preview successful: ${previewData.successCount} valid row(s) found.`)
      }
    } catch (error: any) {
      console.error('Error previewing file:', error)
      toast.error('Failed to preview file')
    } finally {
      setPreviewing(false)
    }
  }

  const handleConfirmUpload = async () => {
    if (isUploadActive) {
      toast.warning('An upload is already in progress. Please wait for it to complete.')
      return
    }
    if (!selectedFile) return

    setUploading(true)
    try {
      const job = await startCrewUploadJob(selectedFile)
      setUploadJob(job)
      setCurrentStep('RESULT')
      if (job?.jobId) {
        localStorage.setItem(STORAGE_KEY, String(job.jobId))
      }
      toast.info('Upload started in background.')
    } catch (error: any) {
      console.error('Error uploading file:', error)
      toast.error('Failed to upload file')
    } finally {
      setUploading(false)
    }
  }

  const handleCancelUpload = async () => {
    if (!uploadJob?.jobId) {
      onClose()
      return
    }
    if (isUploadActive) {
      const confirmed = window.confirm(
        'Cancel the upload in progress? This will stop the current import.'
      )
      if (!confirmed) return
      try {
        await cancelCrewUploadJob(uploadJob.jobId)
        toast.info('Upload cancelled.')
        resetUploadState()
      } catch (error: any) {
        console.error('Error cancelling upload:', error)
        toast.error('Failed to cancel upload')
      }
    } else {
      resetUploadState()
    }
  }

  const downloadErrorsExcel = (errors: BulkUploadError[]) => {
    const ws = XLSX.utils.json_to_sheet(
      errors.map((e, idx) => ({
        'Sr No': idx + 1,
        'Row Number': e.rowNumber,
        'Sheet': e.sheetName,
        'Error Message': e.errorMessage,
      }))
    )
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Errors')
    XLSX.writeFile(wb, `crew_upload_errors_${new Date().getTime()}.xlsx`)
  }

  const downloadErrorsPdf = (errors: BulkUploadError[]) => {
    const doc = new jsPDF()
    doc.text('Crew Bulk Upload Error Report', 14, 15)
    doc.setFontSize(10)
    doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, 22)
    
    const tableData = errors.map((e, idx) => [
      idx + 1,
      e.rowNumber,
      e.sheetName,
      e.errorMessage
    ])

    ;(doc as any).autoTable({
      head: [['Sr No', 'Row', 'Sheet', 'Error Message']],
      body: tableData,
      startY: 30,
      theme: 'grid',
      headStyles: { fillColor: [220, 53, 69] },
    })

    doc.save(`crew_upload_errors_${new Date().getTime()}.pdf`)
  }

  if (!visible) return null

  return (
    <div style={WRAP_STYLE} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div
        className='card shadow-lg border-0'
        style={{
          width: '90%',
          maxWidth: '800px',
          maxHeight: '90vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: '12px',
        }}
      >
        {/* Header */}
        <div className='card-header border-0 pt-7 pb-3 d-flex align-items-center justify-content-between px-9 bg-light'>
          <div className='d-flex align-items-center gap-3'>
            <div
              className='bg-light-primary p-3 rounded-circle d-flex align-items-center justify-content-center'
              style={{ width: '45px', height: '45px' }}
            >
              <KTSVG path='/media/icons/duotune/general/gen005.svg' className='svg-icon-1 svg-icon-primary' />
            </div>
            <h3 className='card-title align-items-start flex-column m-0'>
              <span className='card-label fw-bold fs-3 text-gray-800'>Crew Bulk Upload</span>
              <span className='text-muted mt-1 fw-semibold fs-7 d-block'>
                Import multiple crew members from Excel or CSV
              </span>
            </h3>
          </div>
          <button
            className='btn btn-icon btn-sm btn-active-light-primary rounded-circle'
            onClick={onClose}
          >
            <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-1' />
          </button>
        </div>

        {/* Body */}
        <div className='card-body p-9 overflow-auto'>
          {/* Step Indicators */}
          <div className='d-flex align-items-center justify-content-center mb-10'>
            {[
              { id: 'UPLOAD', label: 'Upload', icon: 'arr031' },
              { id: 'PREVIEW', label: 'Preview', icon: 'gen004' },
              { id: 'RESULT', label: 'Import', icon: 'arr085' },
            ].map((step, idx) => (
              <React.Fragment key={step.id}>
                <div className='d-flex flex-column align-items-center' style={{ width: '80px' }}>
                  <div
                    className={`rounded-circle d-flex align-items-center justify-content-center mb-2 ${
                      currentStep === step.id
                        ? 'bg-primary text-white shadow'
                        : idx < ['UPLOAD', 'PREVIEW', 'RESULT'].indexOf(currentStep)
                        ? 'bg-success text-white'
                        : 'bg-gray-200 text-gray-500'
                    }`}
                    style={{ width: '36px', height: '36px', transition: 'all 0.3s ease' }}
                  >
                    {idx < ['UPLOAD', 'PREVIEW', 'RESULT'].indexOf(currentStep) ? (
                      <KTSVG path='/media/icons/duotune/arrows/arr012.svg' className='svg-icon-3' />
                    ) : (
                      <span className='fw-bold fs-6'>{idx + 1}</span>
                    )}
                  </div>
                  <span
                    className={`fs-8 fw-bold ${
                      currentStep === step.id ? 'text-primary' : 'text-gray-500'
                    }`}
                  >
                    {step.label}
                  </span>
                </div>
                {idx < 2 && (
                  <div
                    className={`flex-grow-1 mx-2 mb-6 h-2px rounded ${
                      idx < ['UPLOAD', 'PREVIEW', 'RESULT'].indexOf(currentStep)
                        ? 'bg-success'
                        : 'bg-gray-200'
                    }`}
                    style={{ maxWidth: '60px' }}
                  />
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Step 1: Upload */}
          {currentStep === 'UPLOAD' && (
            <div className='animate__animated animate__fadeIn'>
              <div
                className='border border-dashed border-primary rounded p-10 d-flex flex-column align-items-center justify-content-center mb-8'
                style={{ backgroundColor: '#f8faff', cursor: 'pointer', minHeight: '200px' }}
                onClick={() => document.getElementById('crew-upload-input')?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault()
                  const file = e.dataTransfer.files?.[0]
                  if (file) {
                    const fakeEvent = { target: { files: [file] } } as any
                    handleFileSelect(fakeEvent)
                  }
                }}
              >
                <input
                  id='crew-upload-input'
                  type='file'
                  className='d-none'
                  accept='.xlsx,.xls,.csv'
                  onChange={handleFileSelect}
                />
                <KTSVG
                  path='/media/icons/duotune/files/fil003.svg'
                  className='svg-icon-5x text-primary mb-4 opacity-75'
                />
                <div className='text-center'>
                  <h4 className='fw-bold text-gray-800 mb-1'>
                    {selectedFile ? selectedFile.name : 'Click or Drag file here to upload'}
                  </h4>
                  <p className='text-gray-500 fs-7'>Supports Excel (.xlsx, .xls) and CSV (.csv)</p>
                  {selectedFile && (
                    <span className='badge badge-light-primary fw-bold px-3 py-2 mt-2'>
                      {(selectedFile.size / 1024).toFixed(1)} KB
                    </span>
                  )}
                </div>
              </div>

              <div className='bg-light-info rounded p-5 d-flex align-items-center mb-8 border border-info border-dashed'>
                <KTSVG path='/media/icons/duotune/general/gen044.svg' className='svg-icon-1 text-info me-5' />
                <div className='flex-grow-1'>
                  <h5 className='text-info fw-bold mb-1 fs-6'>Need the template?</h5>
                  <p className='text-gray-700 fs-7 m-0'>
                    Download our official template to ensure your data is formatted correctly for a smooth import.
                  </p>
                </div>
                <button
                  className='btn btn-sm btn-info fw-bold ms-3 shadow-sm'
                  onClick={handleDownloadTemplate}
                >
                  <KTSVG path='/media/icons/duotune/arrows/arr075.svg' className='svg-icon-3' />
                  Download
                </button>
              </div>

              <div className='d-flex justify-content-end gap-3'>
                <button className='btn btn-light fw-bold px-6' onClick={onClose}>
                  Cancel
                </button>
                <button
                  className='btn btn-primary fw-bold px-8 shadow-sm'
                  disabled={!selectedFile || previewing}
                  onClick={handlePreview}
                >
                  {previewing ? (
                    <>
                      <span className='spinner-border spinner-border-sm align-middle me-2'></span>
                      Validating...
                    </>
                  ) : (
                    <>
                      Preview Import
                      <KTSVG path='/media/icons/duotune/arrows/arr064.svg' className='svg-icon-3 ms-2' />
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Preview */}
          {currentStep === 'PREVIEW' && preview && (
            <div className='animate__animated animate__fadeIn'>
              <div className='alert alert-dismissible bg-light-primary d-flex flex-column flex-sm-row p-5 mb-8 border border-primary border-dashed'>
                <KTSVG path='/media/icons/duotune/general/gen004.svg' className='svg-icon-2hx text-primary me-4 mb-5 mb-sm-0' />
                <div className='d-flex flex-column pe-0 pe-sm-10'>
                  <h4 className='fw-bold text-dark'>Preview Summary</h4>
                  <p className='text-gray-700 fs-6 m-0'>
                    We've analyzed your file. Review the counts below before proceeding with the import.
                  </p>
                </div>
              </div>

              <div className='row g-4 mb-8'>
                {[
                  { label: 'Total Rows', value: preview.totalRows, color: 'primary', icon: 'gen008' },
                  { label: 'Valid Rows', value: preview.successCount, color: 'success', icon: 'arr012' },
                  { label: 'Error Rows', value: preview.errorCount, color: 'danger', icon: 'arr011' },
                  { label: 'Warnings', value: preview.warningCount, color: 'warning', icon: 'gen044' },
                ].map((stat) => (
                  <div key={stat.label} className='col-3'>
                    <div className={`bg-light-${stat.color} rounded p-5 border border-${stat.color} border-opacity-25`}>
                      <div className='d-flex align-items-center mb-2'>
                        <KTSVG path={`/media/icons/duotune/arrows/${stat.icon}.svg`} className={`svg-icon-2 text-${stat.color} me-2`} />
                        <span className={`text-${stat.color} fw-bold fs-7`}>{stat.label}</span>
                      </div>
                      <div className='fs-2 fw-bolder text-gray-800'>{stat.value}</div>
                    </div>
                  </div>
                ))}
              </div>

              {preview.errorCount > 0 && (
                <div className='mb-8 border rounded overflow-hidden'>
                  <div 
                    className='bg-light-danger px-5 py-3 d-flex align-items-center justify-content-between cursor-pointer'
                    onClick={() => setShowPreviewErrors(!showPreviewErrors)}
                  >
                    <div className='d-flex align-items-center'>
                      <KTSVG path='/media/icons/duotune/general/gen040.svg' className='svg-icon-2 text-danger me-2' />
                      <span className='fw-bold text-danger'>View {preview.errorCount} Validation Errors</span>
                    </div>
                    <KTSVG 
                      path={`/media/icons/duotune/arrows/arr0${showPreviewErrors ? '72' : '73'}.svg`} 
                      className='svg-icon-3 text-danger' 
                    />
                  </div>
                  {showPreviewErrors && (
                    <div className='p-0' style={{ maxHeight: '200px', overflowY: 'auto' }}>
                      <table className='table table-row-dashed table-row-gray-300 align-middle gs-5 gy-3 m-0'>
                        <thead className='bg-light sticky-top'>
                          <tr className='fw-bold text-muted'>
                            <th className='min-w-50px'>Row</th>
                            <th className='min-w-200px'>Error Message</th>
                          </tr>
                        </thead>
                        <tbody>
                          {preview.errors.map((err, i) => (
                            <tr key={i}>
                              <td className='fw-bold'>{err.rowNumber}</td>
                              <td className='text-danger fs-7'>{err.errorMessage}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              <div className='d-flex justify-content-between align-items-center mt-10'>
                <button className='btn btn-secondary fw-bold px-6' onClick={() => setCurrentStep('UPLOAD')}>
                  <KTSVG path='/media/icons/duotune/arrows/arr063.svg' className='svg-icon-3 me-2' />
                  Back
                </button>
                <div className='d-flex gap-3'>
                  <button className='btn btn-light-danger fw-bold' onClick={() => downloadErrorsExcel(preview.errors)}>
                    <KTSVG path='/media/icons/duotune/files/fil003.svg' className='svg-icon-3 me-2' />
                    Export Errors
                  </button>
                  <button 
                    className='btn btn-success fw-bold px-10 shadow-sm'
                    disabled={preview.successCount === 0 || uploading}
                    onClick={handleConfirmUpload}
                  >
                    {uploading ? (
                      <>
                        <span className='spinner-border spinner-border-sm align-middle me-2'></span>
                        Processing...
                      </>
                    ) : (
                      <>
                        Start Import ({preview.successCount} Rows)
                        <KTSVG path='/media/icons/duotune/arrows/arr012.svg' className='svg-icon-3 ms-2' />
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Result / Processing */}
          {currentStep === 'RESULT' && uploadJob && (
            <div className='animate__animated animate__fadeIn'>
              <div className='text-center mb-10'>
                <div className='mb-5'>
                  {uploadJob.status === 'RUNNING' || uploadJob.status === 'QUEUED' ? (
                    <div className='position-relative d-inline-block'>
                      <div 
                        className='spinner-border text-primary' 
                        role='status' 
                        style={{ width: '80px', height: '80px', borderWidth: '6px' }}
                      >
                        <span className='visually-hidden'>Loading...</span>
                      </div>
                      <div 
                        className='position-absolute top-50 start-50 translate-middle fw-bolder fs-4 text-primary'
                      >
                        {uploadJob.progress}%
                      </div>
                    </div>
                  ) : uploadJob.status === 'SUCCESS' ? (
                    <div className='bg-light-success rounded-circle p-5 d-inline-block shadow-sm'>
                      <KTSVG path='/media/icons/duotune/arrows/arr012.svg' className='svg-icon-5x text-success' />
                    </div>
                  ) : (
                    <div className='bg-light-danger rounded-circle p-5 d-inline-block shadow-sm'>
                      <KTSVG path='/media/icons/duotune/arrows/arr011.svg' className='svg-icon-5x text-danger' />
                    </div>
                  )}
                </div>

                <h2 className='fw-bold text-gray-800 mb-2'>
                  {uploadJob.status === 'RUNNING' 
                    ? 'Processing Crew Data...' 
                    : uploadJob.status === 'QUEUED'
                    ? 'Waiting in Queue...'
                    : uploadJob.status === 'SUCCESS'
                    ? 'Import Successful!'
                    : 'Import Failed'}
                </h2>
                <p className='text-muted fs-6 mb-8'>
                  {uploadJob.summary || 'Processing your data. This may take a few moments depending on the file size.'}
                </p>
              </div>

              {(uploadJob.status === 'RUNNING' || uploadJob.status === 'QUEUED') && (
                <div className='mb-10'>
                  <div className='d-flex justify-content-between fw-bold fs-6 mb-2'>
                    <span>Progress</span>
                    <span>{uploadJob.processedRows} / {uploadJob.totalRows} Rows</span>
                  </div>
                  <div className='h-12px w-100 bg-gray-200 rounded-pill overflow-hidden mb-3'>
                    <div 
                      className='h-100 bg-primary rounded-pill transition-all duration-500'
                      style={{ width: `${uploadJob.progress}%` }}
                    />
                  </div>
                  <div className='d-flex justify-content-between align-items-center'>
                    <span className='text-muted fs-7'>
                      {uploadJob.status === 'RUNNING' ? 'System is processing rows...' : 'Initializing job...'}
                    </span>
                    {uploadJob.etaSeconds && (
                      <span className='badge badge-light-primary fw-bold py-2'>
                        ETA: {formatEta(uploadJob.etaSeconds)}
                      </span>
                    )}
                  </div>
                </div>
              )}

              {uploadJob.status === 'SUCCESS' && (
                <div className='bg-light-success rounded p-6 border border-success border-dashed mb-10'>
                  <div className='d-flex align-items-center mb-4'>
                    <KTSVG path='/media/icons/duotune/general/gen048.svg' className='svg-icon-1 text-success me-3' />
                    <h5 className='text-success fw-bold m-0'>Summary of Changes</h5>
                  </div>
                  <p className='text-gray-800 fs-6'>
                    The import has completed successfully. <strong>{uploadJob.processedRows}</strong> crew members have been added to the system.
                  </p>
                  <div className='d-flex gap-3 mt-4'>
                    <button className='btn btn-sm btn-success fw-bold shadow-sm' onClick={onClose}>
                      Go to Crew List
                    </button>
                    <button className='btn btn-sm btn-outline-success fw-bold' onClick={resetUploadState}>
                      Upload Another
                    </button>
                  </div>
                </div>
              )}

              {uploadJob.errors && uploadJob.errors.length > 0 && (
                <div className='mb-8 border rounded overflow-hidden shadow-sm'>
                  <div 
                    className='bg-light-danger px-5 py-3 d-flex align-items-center justify-content-between cursor-pointer'
                    onClick={() => setShowJobErrors(!showJobErrors)}
                  >
                    <div className='d-flex align-items-center'>
                      <KTSVG path='/media/icons/duotune/general/gen040.svg' className='svg-icon-2 text-danger me-2' />
                      <span className='fw-bold text-danger'>{uploadJob.errors.length} Processing Errors</span>
                    </div>
                    <KTSVG 
                      path={`/media/icons/duotune/arrows/arr0${showJobErrors ? '72' : '73'}.svg`} 
                      className='svg-icon-3 text-danger' 
                    />
                  </div>
                  {showJobErrors && (
                    <div className='p-0' style={{ maxHeight: '250px', overflowY: 'auto' }}>
                      <table className='table table-row-dashed table-row-gray-300 align-middle gs-5 gy-3 m-0'>
                        <thead className='bg-light sticky-top'>
                          <tr className='fw-bold text-muted'>
                            <th className='min-w-50px text-center'>Row</th>
                            <th className='min-w-200px'>Error Details</th>
                          </tr>
                        </thead>
                        <tbody>
                          {uploadJob.errors.map((err, i) => (
                            <tr key={i}>
                              <td className='fw-bold text-center'>{err.rowNumber}</td>
                              <td className='text-danger fs-7'>{err.errorMessage}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  <div className='bg-light p-3 border-top d-flex justify-content-end'>
                    <button className='btn btn-sm btn-light-danger me-2' onClick={() => downloadErrorsPdf(uploadJob.errors || [])}>
                      Download PDF
                    </button>
                    <button className='btn btn-sm btn-danger' onClick={() => downloadErrorsExcel(uploadJob.errors || [])}>
                      Download Excel
                    </button>
                  </div>
                </div>
              )}

              <div className='d-flex justify-content-center mt-10'>
                {isUploadActive ? (
                  <button className='btn btn-light-danger fw-bold' onClick={handleCancelUpload}>
                    <KTSVG path='/media/icons/duotune/arrows/arr011.svg' className='svg-icon-3 me-2' />
                    Cancel Import
                  </button>
                ) : (
                  uploadJob.status !== 'SUCCESS' && (
                    <button className='btn btn-secondary fw-bold' onClick={resetUploadState}>
                      Try Again
                    </button>
                  )
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
